import * as React from "react";
import {
  BrowserRouter,
  Route,
  Switch,
  Redirect,
  useHistory,
  useLocation,
} from "react-router-dom";
import { ReactNode } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import Register from "../containers/Auth/Register";
import SetupWizard from "../containers/Setup";
import ForgotPassword from "../containers/Auth/ForgotPassword";
import { IUser } from "../types/user";
import { Mixpanel } from "../util/mixpanel";
import { RootState } from "../redux/store";
import "./style.css";
import { useEffect } from "react";
import CommunityChat from "../screens/User/Chat";
import ChatDashboard from "../screens/User/Chat/Dashboard";
import ClientDashboard from "../screens/User/ClientDashboard";
import DefaultScreen from "../components/DefaultScreen";
import PostScreen from "../components/PostScreen";
import ChannelScreen from "../components/ChannelScreen/index";
import ChannelSetting from "../components/ChannelScreen/setting";
import ChannelSearch from "../components/ChannelScreen/search";
import Profile from "../screens/User/Profile";
import ActionItems from "../screens/User/ActionItems";
import Sidebar from "../screens/User/Sidebar";
import { isAdmin } from "../util/helpers";
import AdminSideBar from "../screens/Admin/Sidebar";
import ProgramList from "../screens/Admin/ProgramList/List";
import ProgramDrillDown from "../screens/Admin/ProgramList/ProgramDrillDown";
import MembersList from "../screens/Admin/ProgramList/ProgramDrillDown/MemberList";
import UserRecord from "../screens/Admin/ProgramList/ProgramDrillDown/UserRecord";
import SetPassword from "../screens/OnBoarding/Auth/SetPassword";
import Login from "../screens/OnBoarding/Auth/Login/index";
import ProtectedRoute from "./ProtectedRoute";
import { UserType } from "../constants/data";
import { SocketProvider } from "../provider/socketProvider";
import { DEFAULT_CHANNEL_THEME, sanitizeChannelTheme } from "../util/theme";
import AppSetting from "../components/Setting";
import AppNotificationSetting from "../components/Setting/AppNotificationSetting";
import DiscoverCommunity from "../components/CommunityScreen/discoverCommunity";
import EditProfile from "../components/Account/editProfile";
import OnBoardingScreen from "../containers/Auth/Onboarding";
import ChannelList from "../components/ChannelScreen/list";
import { getEnrolledChannels } from "../redux/actions/programAction";
import { getUserProgramEnrollmentData } from "../redux/actions/programEnrollmentAction";
import CreatePost from "../components/PostScreen/create";
import AdminChannelManagment from "../screens/Admin/AdminChannelManagment";
import AdminChannelEditor from "../screens/Admin/AdminChannelEditor";
import AdminCommunityThemeEditor from "../screens/Admin/AdminCommunityThemeEditor";
import AdminCommunityMembers from "../screens/Admin/AdminCommunityRoles";
import AdminInvitations from "../screens/Admin/AdminInvitations";
import TestManagement from "../screens/Admin/TestManagement";
import ContentLogs from "../screens/Admin/ContentLogs";
import Analytics from "../screens/Admin/Analytics";

// Fallback theme when communityStyle not set
import { IconButton, useTheme } from "@material-ui/core";
import { usePrivileges } from "../util/usePrivileges";
import { useIsMobile } from "../util/hooks";
import { handleDrawer } from "../redux/actions/drawerAction";
import HamburgerIcon from "../components/Icons/HamburgerIcon";

interface PropsType {
  userStatus: boolean;
  user: IUser;
}

const Navigation = (props: PropsType) => {
  const dispatch = useDispatch();
  const isFirstTimeLogin = () => props.user?.isFirstTimeLogin || false;

  return (
    <SocketProvider userId={localStorage.getItem("user")}>
      <BrowserRouter>
        <Switch>
          {/* Public routes */}
          <Route exact path="/login" component={Login} />
          <Route exact path="/register" component={Register} />
          <Route exact path="/setup" component={SetupWizard} />
          <Route exact path="/forgot-password" component={ForgotPassword} />
          <Route exact path="/set-password">
            {props.userStatus && isFirstTimeLogin() && <SetPassword />}
          </Route>

          {/* 🚫 Never allow admin landing pages */}
          <Route exact path="/admin/dashboard" render={() => <Redirect to="/user/dashboard" />} />
          <Route exact path="/admin" render={() => <Redirect to="/user/dashboard" />} />

          {/* 1) AdminChannelEditor: no sidebar */}
          <ProtectedRoute
            exact
            path="/admin/programs/:programId/communities/:communityIdentifier/channels/:channelIdentifier"
            component={AdminChannelEditor}
            requiredRole={[UserType.moderator, UserType.admin, UserType.teamAdmin, UserType.communityAdmin]}
          />

          {/* AdminCommunityThemeEditor: no sidebar */}
          <ProtectedRoute
            exact
            path="/admin/programs/:programId/communities/:communityIdentifier/theme"
            component={AdminCommunityThemeEditor}
            requiredRole={[UserType.moderator, UserType.admin, UserType.teamAdmin, UserType.communityAdmin]}
          />

          {/* 2) AdminChannelManagment: no sidebar */}
          <ProtectedRoute
            exact
            path="/admin/channel/list"
            component={AdminChannelManagment}
            requiredRole={[UserType.member, UserType.moderator, UserType.teamAdmin, UserType.communityAdmin, UserType.admin]}
          />

          <ProtectedRoute
            exact
            path="/admin/community/roles"
            component={AdminCommunityMembers}
            requiredRole={[UserType.member, UserType.moderator, UserType.teamAdmin, UserType.communityAdmin, UserType.admin]}
          />

          <ProtectedRoute
            exact
            path="/admin/invitations"
            component={AdminInvitations}
            requiredRole={[UserType.moderator, UserType.teamAdmin, UserType.communityAdmin, UserType.admin]}
          />

          <ProtectedRoute
            exact
            path="/admin/analytics"
            component={Analytics}
            requiredRole={[UserType.admin, UserType.communityAdmin, UserType.moderator]}
          />

          <ProtectedRoute
            exact
            path="/admin/test-management"
            component={TestManagement}
            requiredRole={[UserType.admin]}
          />

          <ProtectedRoute
            exact
            path="/admin/content-logs"
            component={ContentLogs}
            requiredRole={[UserType.admin]}
          />

          {/* 3) Other admin routes under AdminLayout */}
          <ProtectedRoute
            path="/admin"
            component={() => (
              <AdminLayout>
                <Switch>
                  {/* 🔁 Replace admin dashboard with redirect */}
                  <Redirect exact from="/admin/dashboard" to="/user/dashboard" />

                  {/* keep deep admin tools if needed */}
                  <Route exact path="/admin/dashboard/program/:programId" component={ProgramDrillDown} />
                  <Route exact path="/admin/dashboard/program/:programId/team/:teamId" component={MembersList} />
                  <Route exact path="/admin/dashboard/userRecord/:userId" component={UserRecord} />
                  <Route exact path="/admin/channels" component={ChannelList} />

                  {/* and never default /admin to the dashboard */}
                  <Redirect from="/admin" to="/user/dashboard" />
                </Switch>
              </AdminLayout>
            )}
            requiredRole={[UserType.admin, UserType.teamAdmin, UserType.communityAdmin]}
          />

          {/* Onboarding */}
          <ProtectedRoute
            path="/onboarding"
            component={() => (
              <Switch>
                <Route exact path="/onboarding" component={OnBoardingScreen} />
                <Redirect from="/onboarding" to="/onboarding" />
              </Switch>
            )}
            requiredRole={[
              UserType.member,
              UserType.client,
              UserType.coach,
              UserType.teamAdmin,
              UserType.moderator,
              UserType.communityAdmin,
              UserType.admin,
            ]}
          />

          {/* User routes under HomeLayout */}
          <ProtectedRoute
            path="/user"
            component={() => (
              <HomeLayout>
                <Switch>
                  <Route exact path="/user/dashboard" component={ClientDashboard} />
                  <Route exact path="/user/chat-dashboard" component={ChatDashboard} />
                  <Route exact path="/user/chat" component={CommunityChat} />
                  <Route exact path="/user/general" component={DefaultScreen} />
                  <Route exact path="/user/payment" component={DefaultScreen} />
                  <Route exact path="/user/subscription" component={DefaultScreen} />
                  <Route exact path="/user/support" component={DefaultScreen} />
                  <Route exact path="/user/post" component={PostScreen} />
                  <Route exact path="/user/post/create" component={CreatePost} />
                  <Route exact path="/user/channel" component={ChannelScreen} />
                  <Route exact path="/user/channel/setting" component={ChannelSetting} />
                  <Route exact path="/user/channel/search" component={ChannelScreen} />
                  <Route exact path="/user/channels" component={ChannelList} />
                  <Route exact path="/user/appSetting" component={AppSetting} />
                  <Route exact path="/user/appNotificationSetting" component={AppNotificationSetting} />
                  <Route exact path="/user/communities/discover" component={DiscoverCommunity} />
                  <Route exact path="/user/profile" component={Profile} />
                  <Route exact path="/user/editProfile" component={EditProfile} />
                  <Route exact path="/user/action-items" component={ActionItems} />
                  <Redirect from="/user" to="/user/dashboard" />
                </Switch>
              </HomeLayout>
            )}
            requiredRole={[
              UserType.member,
              UserType.client,
              UserType.coach,
              UserType.teamAdmin,
              UserType.moderator,
              UserType.communityAdmin,
              UserType.admin
            ]}
          />

          {/* Default redirects */}
          <Route
            exact
            path="/"
            render={() => (props.userStatus ? <Redirect to="/user/dashboard" /> : <Redirect to="/login" />)}
          />
          <Route render={() => <Redirect to="/login" />} />
        </Switch>
      </BrowserRouter>
    </SocketProvider>
  );
};

function HomeLayout(props: { children?: ReactNode }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const muiTheme = useTheme();
  const isMobile = useIsMobile();
  const isDrawerOpen = useSelector((s: RootState) => s.drawerReducer.isOpen);

  // pull program data from the correct slice
  const { singleProgram, subscribeChannels } = useSelector((s: RootState) => s.programReducer);

  // resolve community -> communityStyle like ClientDashboard does
  const { communityIdentifier, programId, userId } = JSON.parse(
    localStorage.getItem("activeProgramEnrollment") || "{}"
  );
  const community = singleProgram?.communities?.find(
    (c: any) => c?.communityIdentifier === communityIdentifier
  );
  const style = (community as any)?.communityStyle;
  const communityTheme = React.useMemo(
    () => sanitizeChannelTheme(DEFAULT_CHANNEL_THEME, style),
    [style]
  );
  const bg = communityTheme.secondaryColor || muiTheme.palette.background.default;

  const { usePrivileges } = require("../util/usePrivileges"); // if needed by bundler order
  const { privileges } = usePrivileges(communityIdentifier || undefined);

  const activeSubscriptions = React.useMemo(
    () =>
      (subscribeChannels || []).filter((sub: any) => {
        if (!sub?.isActive) return false;
        const targetCommunity = sub?.communityIdentifier || communityIdentifier || '';
        const targetChannel = sub?.channelIdentifier || '';
        if (!targetChannel) return false;
        return privileges.canViewChannel(targetCommunity, targetChannel);
      }),
    [subscribeChannels, privileges, communityIdentifier]
  );
  const activeSubscriptionCount = activeSubscriptions.length;

  useEffect(() => {
    const prevBody = document.body.style.background;
    const prevHtml = document.documentElement.style.background;
    document.body.style.background = bg || "";
    document.documentElement.style.background = bg || "";
    return () => {
      document.body.style.background = prevBody;
      document.documentElement.style.background = prevHtml;
    };
  }, [bg]);

  const [loading, setLoading] = React.useState(() => activeSubscriptionCount === 0);

  useEffect(() => {
    if (!programId || !userId || !communityIdentifier) {
      setLoading(false);
      return;
    }

    if (activeSubscriptionCount > 0) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const channels = await dispatch(
          getEnrolledChannels(programId, userId, communityIdentifier)
        );
        if (!isMounted) return;
        if (Array.isArray(channels) && channels.length === 0) {
          history.push("/onboarding");
        } else {
          setLoading(false);
        }
      } catch (err) {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [
    programId,
    userId,
    communityIdentifier,
    dispatch,
    history,
    activeSubscriptionCount,
  ]);

  useEffect(() => {
    if (!programId || !userId) return;
    dispatch(getUserProgramEnrollmentData(programId, userId));
  }, [dispatch, programId, userId]);

  if (loading) return <div className="loader-container"><div className="loader" /></div>;
  return (
    <div className="wrapper" style={{ background: bg }}>
      <Sidebar />
      <div className="main_cnt" style={{ background: bg, minHeight: "100vh" }}>
        {isMobile && !isDrawerOpen && (
          <IconButton
            aria-label="Open navigation menu"
            onClick={() => dispatch(handleDrawer(true))}
            style={{
              position: "fixed",
              top: 12,
              left: 12,
              zIndex: 1300,
              background: communityTheme.mainColor || "#ffffff",
              color: "#ffffff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            }}
          >
            <HamburgerIcon />
          </IconButton>
        )}
        {props.children}
      </div>
    </div>
  );
}

function AdminLayout(props: { children?: ReactNode }) {
  const history = useHistory();
  const location = useLocation();
  const muiTheme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const isDrawerOpen = useSelector((s: RootState) => s.drawerReducer.isOpen);

  // pull program data from the correct slice
  const { singleProgram } = useSelector((s: RootState) => s.programReducer);

  // resolve community -> communityStyle (same approach as HomeLayout)
  const { communityIdentifier } = JSON.parse(
    localStorage.getItem("activeProgramEnrollment") || "{}"
  );
  const community = singleProgram?.communities?.find(
    (c: any) => c?.communityIdentifier === communityIdentifier
  );
  const style = (community as any)?.communityStyle;
  const communityTheme = React.useMemo(
    () => sanitizeChannelTheme(DEFAULT_CHANNEL_THEME, style),
    [style]
  );
  const bg = communityTheme.secondaryColor || muiTheme.palette.background.default;

  const { programId, userId } = JSON.parse(
    localStorage.getItem("activeProgramEnrollment") || "{}"
  );

  React.useEffect(() => {
    if (!programId || !userId) return;
    dispatch(getUserProgramEnrollmentData(programId, userId));
  }, [dispatch, programId, userId]);

  React.useEffect(() => {
    if (!programId || !userId || !communityIdentifier) return;
    dispatch(getEnrolledChannels(programId, userId, communityIdentifier));
  }, [dispatch, programId, userId, communityIdentifier, location.pathname, location.search]);

  useEffect(() => {
    if (!isAdmin()) history.push(`/user/dashboard`);
  }, [history]);

  useEffect(() => {
    const prevBody = document.body.style.background;
    const prevHtml = document.documentElement.style.background;
    document.body.style.background = bg || "";
    document.documentElement.style.background = bg || "";
    return () => {
      document.body.style.background = prevBody;
      document.documentElement.style.background = prevHtml;
    };
  }, [bg]);

  return (
    <div className="wrapper" style={{ background: bg }}>
      <AdminSideBar />
      <div className="main_cnt" style={{ background: bg, minHeight: "100vh" }}>
        {isMobile && !isDrawerOpen && (
          <IconButton
            aria-label="Open navigation menu"
            onClick={() => dispatch(handleDrawer(true))}
            style={{
              position: "fixed",
              top: 12,
              left: 12,
              zIndex: 1300,
              background: communityTheme.mainColor || "#ffffff",
              color: "#ffffff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            }}
          >
            <HamburgerIcon />
          </IconButton>
        )}
        {props.children}
      </div>
    </div>
  );
}

const mapStateToProps = (store: RootState) => ({
  userStatus: store.auth.userStatus,
  user: store.auth.user,
});

export default connect(mapStateToProps)(Navigation);
